import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useThrottledCallback, useDebouncedCallback } from 'use-debounce';

import cn from 'classnames';

import { useRouter } from 'next/router';
import Link from 'next/link';
import Image from 'next/image';

import { isServer } from '@constants/index';
import { Button, Divider } from '@components/index';
import Text from '@components/Text';
import Loader from '@components/Loader';

import ProductCard from '@modules/products/components/ProductCard';

import useSearch from '@hooks/useSearch';
import useTagsLabels from '@hooks/useTagsLabels';

import s from './SearchContent.module.scss';
import isValidLabs from '@utils/isValidLabs';

const SearchContent = () => {
  const router = useRouter();

  const [searchValue, setSearchValue] = useState<string>(router?.query?.search ? String(router?.query?.search) : '');

  const [isShowProducts, setIsShowProducts] = useState<boolean>(false);

  const [isLoading, setIsLoading] = useState<boolean>(true);

  const { onFindProducts, onFetchMore, products, isFetchingMore, loadingSearch } = useSearch();

  const { tagsLabels } = useTagsLabels(Number(router.query.category) || 1);

  const routerCategory = router?.query.category;
  const routerSubCategory = router?.query.sub_category;
  const handleScrolledToEnd = useThrottledCallback(() => {
    onFetchMore();
  }, 1000);

  useEffect(() => {
    if (isServer) {
      return;
    }
    window.onscroll = function () {
      if (window.innerHeight + Math.ceil(window.pageYOffset) >= document.body.offsetHeight && isShowProducts) {
        handleScrolledToEnd();
      }
    };
  }, [handleScrolledToEnd, isShowProducts]);

  const getLabel = useCallback(() => {
    if (!routerCategory) return [];
    return routerSubCategory
      ? [String(routerSubCategory)]
      : [String(routerCategory), ...tagsLabels.map((tag) => tag.id)];
  }, [routerSubCategory, routerCategory, tagsLabels]);

  const handleFindProductsDebounced = useDebouncedCallback(async () => {
    await onFindProducts(String(searchValue), getLabel());
    setIsLoading(false);
  }, 275);

  const handleFindProducts = useDebouncedCallback(async () => {
    setIsLoading(true);
    await onFindProducts(String(searchValue), getLabel());
    setIsLoading(false);
  }, 0);
  useEffect(() => {
    if (searchValue.length > 0) {
      handleFindProductsDebounced();

      setIsShowProducts(true);
    } else if (routerCategory || routerSubCategory) {
      handleFindProducts();

      setIsShowProducts(true);
    } else {
      handleFindProducts();

      setIsShowProducts(false);
    }
  }, [
    routerCategory,
    routerSubCategory,
    searchValue,
    onFindProducts,
    handleFindProducts,
    handleFindProductsDebounced,
    tagsLabels,
  ]);

  useEffect(() => {
    if (router.query) {
      setSearchValue(router?.query.search ? String(router.query.search) : '');
    }
  }, [router.query]);

  const productZeroTitle = useMemo(
    () =>
      !!searchValue && !loadingSearch && !isLoading ? (
        <Text size="extra-large">По вашему запросу ничего не найдено :(</Text>
      ) : null,
    [searchValue, loadingSearch, isLoading]
  );

  return (
    <div className={s.root}>
      {!!tagsLabels.length && (
        <div className={s.subLabels}>
          {tagsLabels.map((tag) => {
            const isActive = router?.query.sub_category === tag.id;
            return (
              <div key={tag.id} className={s.buttonTagWrapper}>
                <Link href={`/search?category=${router?.query.category}&sub_category=${tag.id}`}>
                  <a className={s.buttonTagLink}>
                    <Button className={cn(s.buttonTag, isActive ? s.active : '')} theme="ghost">
                      <div className={s.tagImage}>
                        <Image
                          alt=""
                          src={tag.image?.src || '/images/product-card-image.webp'}
                          layout="fill"
                          objectFit={'cover'}
                        />
                      </div>
                      {tag.title}
                    </Button>
                  </a>
                </Link>
              </div>
            );
          })}
        </div>
      )}
      <div className={s.products}>
        {!!products?.length ? (
          <>
            {products
              .filter((product) => isValidLabs(product))
              .map((product, index) => (
                <>
                  <ProductCard key={product.id} {...product} />
                  {index + 1 !== products.length && <Divider className={s.divider} />}
                </>
              ))}

            {isFetchingMore ? <Loader className={s.loader} /> : null}
          </>
        ) : (
          productZeroTitle
        )}

        {isLoading ? <Loader className={s.loader} variant="logo" /> : null}
      </div>
    </div>
  );
};

export default SearchContent;
